import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { listServicePeople } from '@/store/permissions/permissions.api';

import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';
import {
  SEAT_POOLS,
  byOwnerFirst,
  seatCapacity,
  seatPeopleOf,
  seatTypeFor,
  seatsHeldForOwner,
} from './vessel-seats';
import type { SeatPerson, SeatPool } from './vessel-seats';

// One page covers a vessel's people - a crew list is nowhere near this long.
const PEOPLE_PAGE_SIZE = 200;

/** One seat pool, ready to render: how many seats it holds and who is in them. */
export type SeatPoolState = {
  pool: SeatPool;
  capacity: number;
  /**
   * Seats within `capacity` that are paid for and deliberately empty: the management seat a
   * captain is holding for the owner they said they would add. Nobody can be put in one.
   */
  heldForOwner: number;
  occupants: SeatPerson[];
};

export type VesselSeats = {
  pools: SeatPoolState[];
  occupied: number;
  capacity: number;
  isLoading: boolean;
  error: unknown;
};

/**
 * The vessel's seats, joined from its subscription (how many seats are paid for) and its
 * people (who occupies them).
 *
 * Occupancy comes from `GET /people?service_id=…`, whose `services[]` entry per person
 * carries their `seat_type` - the one call that reports it. `GET /crew` does not.
 */
export const useVesselSeats = (item: ManagedVesselSubscription): VesselSeats => {
  const serviceId = item.vessel.id;
  const { vessel, subscription } = item;

  const peopleQuery = useQuery({
    queryKey: ['vessel-seat-people', serviceId],
    queryFn: () => listServicePeople(serviceId, { limit: PEOPLE_PAGE_SIZE }),
  });

  const people = peopleQuery.data?.items;

  return useMemo(() => {
    const seatPeople = seatPeopleOf(people ?? [], serviceId);
    const pools = SEAT_POOLS.map((pool) => {
      const seatType = seatTypeFor(subscription?.tier ?? null, pool);
      return {
        pool,
        capacity: seatCapacity(subscription, pool),
        heldForOwner: seatsHeldForOwner(vessel, subscription, pool),
        occupants: seatType
          ? seatPeople.filter((person) => person.seatType === seatType).sort(byOwnerFirst)
          : [],
      };
    });

    return {
      pools,
      occupied: pools.reduce((total, { occupants }) => total + occupants.length, 0),
      capacity: pools.reduce((total, { capacity }) => total + capacity, 0),
      isLoading: peopleQuery.isPending,
      error: peopleQuery.error,
    };
  }, [vessel, subscription, serviceId, people, peopleQuery.isPending, peopleQuery.error]);
};
